import { useQuery, useMutation } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { Card, Form, Button, Row, Col, Spinner } from "react-bootstrap";
import { useNavigate, useBlocker, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faBuilding,
  faMapMarkerAlt,
  faInfoCircle,
  faCircleNotch,
  faUser,
  faCheckCircle,
  faSearch,
  faShoppingCart,
  faTrash,
  faUnlock,
} from "@fortawesome/free-solid-svg-icons";
import { useToast } from "providers/ToastProvider";
import { usePermissionModal } from "contexts/PermissionModalContext";
import {
  AlterarFornecedorDtoType,
  EnderecoType,
  TipoPessoa,
  fornecedorBreadcrumbItems,
} from "data/fornecedores";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import DeleteRestoreModal from "../../../components/DeleteRestoreModal";
import DeletedItemBanner from "components/common/DeletedItemBanner";
import DeletedBorderCard from "components/common/DeletedBorderCard";
import { MunicipioType } from "data/municipio";
import MunicipioSelector from "components/common/MunicipioSelector";

// Interface para os dados do fornecedor carregado da API
interface FornecedorData extends AlterarFornecedorDtoType {
  id: number;
  codigo: number;
  ativo: boolean;
  isDeleted?: boolean;
  deletedAt?: string | Date;
}

const EditarFornecedor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getMutationErrorHandler } = usePermissionModal();
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [tipoPessoaSelecionado, setTipoPessoaSelecionado] =
    useState<TipoPessoa>(TipoPessoa.Juridica);
  const [showMunicipioSelector, setShowMunicipioSelector] = useState(false);
  const [modalOperation, setModalOperation] = useState<
    null | "delete" | "restore"
  >(null);

  // Funções de formatação
  const formatCnpj = (value: string) => {
    if (!value) return value;
    const cnpjNumbers = value.replace(/\D/g, "");
    if (cnpjNumbers.length <= 14) {
      if (cnpjNumbers.length === 14) {
        return cnpjNumbers.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
          "$1.$2.$3/$4-$5"
        );
      }
      return cnpjNumbers
        .replace(/^(\d{2})/, "$1.")
        .replace(/^(\d{2})\.(\d{3})/, "$1.$2.")
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})/, "$1.$2.$3/")
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})/, "$1.$2.$3/$4-");
    }
    return cnpjNumbers.substring(0, 14);
  };

  const formatCpf = (value: string) => {
    if (!value) return value;
    const cpfNumbers = value.replace(/\D/g, "");
    if (cpfNumbers.length <= 11) {
      if (cpfNumbers.length === 11) {
        return cpfNumbers.replace(
          /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
          "$1.$2.$3-$4"
        );
      }
      return cpfNumbers
        .replace(/^(\d{3})/, "$1.")
        .replace(/^(\d{3})\.(\d{3})/, "$1.$2.")
        .replace(/^(\d{3})\.(\d{3})\.(\d{3})/, "$1.$2.$3-");
    }
    return cpfNumbers.substring(0, 11);
  };

  const formatTelefone = (value: string) => {
    if (!value) return value;
    const telefoneNumbers = value.replace(/\D/g, "");
    if (telefoneNumbers.length <= 11) {
      if (telefoneNumbers.length === 11) {
        return telefoneNumbers.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
      } else if (telefoneNumbers.length === 10) {
        return telefoneNumbers.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
      }
      return telefoneNumbers
        .replace(/^(\d{2})/, "($1) ")
        .replace(/^(\d{2})\s(\d{4,5})/, "($1) $2-");
    }
    return telefoneNumbers.substring(0, 11);
  };

  const formatCep = (value: string) => {
    if (!value) return value;
    const cepNumbers = value.replace(/\D/g, "");
    if (cepNumbers.length <= 8) {
      if (cepNumbers.length === 8) {
        return cepNumbers.replace(/^(\d{5})(\d{3})$/, "$1-$2");
      }
      return cepNumbers.replace(/^(\d{5})/, "$1-");
    }
    return cepNumbers.substring(0, 8);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";

    // Se a data já está no formato YYYY-MM-DD, mantém
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }

    // Se a data está no formato ISO (2025-07-16T03:00:00Z), converte para YYYY-MM-DD
    if (dateString.includes('T') || dateString.includes('Z')) {
      try {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          // Retorna no formato YYYY-MM-DD para o input type="date"
          return date.toISOString().split('T')[0];
        }
      } catch (error) {

      }
    }

    // Se a data está no formato DD/MM/YYYY, converte para YYYY-MM-DD
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }

    return dateString;
  };

  // Query para buscar dados do fornecedor
  const {
    isLoading,
    isError,
    data: fornecedorData,
    error,
    refetch,
  } = useQuery<FornecedorData>({
    queryKey: ["fornecedor", id],
    queryFn: async () => {
      const response = await axios.get(`/api/fornecedor/${id}`);
      const rawData = response.data;

      if (!rawData) {
        throw new Error(response.data.message || "Erro ao carregar fornecedor");
      }

      // Formate os dados aqui para evitar problemas com dirty state
      const formattedData = {
        ...rawData,
        cnpj: rawData.cnpj ? formatCnpj(rawData.cnpj) : rawData.cnpj,
        cpf: rawData.cpf ? formatCpf(rawData.cpf) : rawData.cpf,
        telefone: rawData.telefone ? formatTelefone(rawData.telefone) : rawData.telefone,
        celular: rawData.celular ? formatTelefone(rawData.celular) : rawData.celular,
        dataNascimento: rawData.dataNascimento ? formatDate(rawData.dataNascimento) : rawData.dataNascimento,
        endereco: rawData.endereco ? {
          ...rawData.endereco,
          cep: rawData.endereco.cep ? formatCep(rawData.endereco.cep) : rawData.endereco.cep
        } : rawData.endereco
      };

      return formattedData;
    },
    enabled: !!id,
  });

  // Configurar o formulário usando react-hook-form
  const form = useForm<AlterarFornecedorDtoType>({
    resetOptions: { keepDirtyValues: true },
    values: fornecedorData
      ? {
        nome: fornecedorData.nome || "",
        nomeFantasia: fornecedorData.nomeFantasia || "",
        razaoSocial: fornecedorData.razaoSocial || "",
        cnpj: fornecedorData.cnpj || "",
        cpf: fornecedorData.cpf || "",
        inscricaoEstadual: fornecedorData.inscricaoEstadual || "",
        telefone: fornecedorData.telefone || "",
        celular: fornecedorData.celular || "",
        email: fornecedorData.email || "",
        dataNascimento: fornecedorData.dataNascimento || "",
        endereco: {
          cep: fornecedorData.endereco?.cep || "",
          logradouro: fornecedorData.endereco?.logradouro || "",
          numero: fornecedorData.endereco?.numero || "",
          complemento: fornecedorData.endereco?.complemento || "",
          bairro: fornecedorData.endereco?.bairro || "",
          municipioId: fornecedorData.endereco?.municipioId || 0,
          municipio: fornecedorData.endereco?.municipio || "",
          uf: fornecedorData.endereco?.uf || "",
        },
        tipoPessoa: fornecedorData.tipoPessoa || TipoPessoa.Juridica,
        isCliente: fornecedorData.isCliente || false,
        isFornecedorProduto: fornecedorData.isFornecedorProduto ?? true,
        prazoEntrega: fornecedorData.prazoEntrega,
        valorMinimo: fornecedorData.valorMinimo,
      }
      : undefined,
  });

  // Observar o estado do checkbox "Fornecedor de Produto"
  const isFornecedorProduto = form.watch("isFornecedorProduto");

  // Query para buscar dados do fornecedor pelo CNPJ
  const cnpjWatch = form.watch("cnpj");

  // Atualizar tipo de pessoa quando os dados do fornecedor são carregados
  useEffect(() => {
    if (fornecedorData?.tipoPessoa) {
      setTipoPessoaSelecionado(fornecedorData.tipoPessoa);
    }
  }, [fornecedorData]);

  // Sincronizar tipoPessoaSelecionado com o valor do formulário
  useEffect(() => {
    form.setValue("tipoPessoa", tipoPessoaSelecionado, { shouldDirty: true });
  }, [tipoPessoaSelecionado]);

  const isDirtyRef = useRef(false);

  useEffect(() => {
    isDirtyRef.current = form.formState.isDirty;
  }, [form.formState.isDirty]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!isDirtyRef.current) return false;
    if (form.formState.isSubmitting) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;

    setPendingNavigation(nextLocation.pathname);
    setShowNavigationModal(true);

    return true;
  });

  // Função para lidar com confirmação de navegação
  const handleNavigationConfirm = () => {
    setShowNavigationModal(false);

    if (pendingNavigation) {
      // Reset dirty state before navigating
      isDirtyRef.current = false;

      // Unblock and navigate
      blocker.proceed?.();
      navigate(pendingNavigation);
    }
  };

  // Função para cancelar navegação
  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    blocker.reset?.();
  };

  // Mutação para atualizar fornecedor
  const updateFornecedorMutation = useMutation({
    mutationFn: async (formData: AlterarFornecedorDtoType) => {
      let fornecedorData: any = {
        ...formData,
        telefone: formData.telefone.replace(/\D/g, ""),
        celular: formData.celular
          ? formData.celular.replace(/\D/g, "")
          : undefined,
        dataNascimento: formData.dataNascimento || undefined,
        endereco: {
          cep: formData.endereco.cep.replace(/\D/g, ""),
          logradouro: formData.endereco.logradouro,
          numero: formData.endereco.numero,
          complemento: formData.endereco.complemento,
          bairro: formData.endereco.bairro,
          municipioId: formData.endereco.municipioId,
          // Não enviar municipio e uf, apenas municipioId
        },
      };

      if (formData.tipoPessoa === TipoPessoa.Juridica) {
        fornecedorData.cnpj = formData.cnpj
          ? formData.cnpj.replace(/\D/g, "")
          : undefined;
        fornecedorData.razaoSocial = formData.razaoSocial;
      } else if (formData.tipoPessoa === TipoPessoa.Fisica) {
        fornecedorData.cpf = formData.cpf
          ? formData.cpf.replace(/\D/g, "")
          : undefined;
      }

      return axios.put(`/api/fornecedor/${id}`, fornecedorData);
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para editar fornecedores",
      (error) => {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError<any>;
          if (axiosError.response?.data?.errors) {
            const errors = axiosError.response.data.errors;
            let errorMessages: string[] = [];

            // Limpar erros anteriores antes de setar novos
            form.clearErrors();

            Object.keys(errors).forEach((key) => {
              if (key.startsWith("Endereco.")) {
                const enderecoField = key
                  .replace("Endereco.", "")
                  .toLowerCase();
                form.setError(`endereco.${enderecoField}` as any, {
                  type: "manual",
                  message: errors[key],
                });
                errorMessages.push(`${enderecoField}: ${errors[key]}`);
              } else {
                const keyLower = key.toLowerCase();
                form.setError(keyLower as any, {
                  type: "manual",
                  message: errors[key],
                });
                errorMessages.push(`${key}: ${errors[key]}`);
              }
            });

            // Exibir mensagens de erro no toast apenas se houver mensagens
            if (errorMessages.length > 0) {
              showToast(
                <>
                  <strong>Erro ao atualizar fornecedor</strong>
                  <ul className="mb-0 mt-1 ps-3">
                    {errorMessages.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                </>,
                "error"
              );
            }

            // Reset da mutation para permitir nova tentativa
            updateFornecedorMutation.reset();
          } else {
            showToast(
              <>
                <strong>Erro ao atualizar fornecedor</strong>
                <p className="mb-0 mt-1">
                  {axiosError.response?.data?.message ||
                    "Verifique os dados e tente novamente."}
                </p>
              </>,
              "error"
            );

            // Reset da mutation para permitir nova tentativa
            updateFornecedorMutation.reset();
          }
        }
      }
    ),
    onSuccess: (response) => {

      const codigoFornecedor = response.data.data?.codigo;

      form.reset(form.getValues(), {
        keepValues: true,
        keepDirtyValues: false,
      });

      isDirtyRef.current = false;

      navigate("/cadastros/fornecedores", {
        state: {
          successCode: codigoFornecedor,
          isNew: false,
          timestamp: new Date().getTime(),
        },
      });

      showToast(
        <>
          Fornecedor <strong>{codigoFornecedor}</strong> atualizado com
          sucesso!
        </>,
        "success"
      );
    },
  });

  useEffect(() => {
    if (!updateFornecedorMutation.isPending) {
      // If the mutation was successful, ensure the form is not marked as dirty
      if (updateFornecedorMutation.isSuccess) {
        isDirtyRef.current = false;
      }
    }
  }, [updateFornecedorMutation.isPending, updateFornecedorMutation.isSuccess]);


  // Mutação para excluir/restaurar fornecedor
  const deleteFornecedorMutation = useMutation({
    mutationFn: async () => {
      return axios.delete(`/api/fornecedor/${id}`);
    },
    onError: getMutationErrorHandler(
      `Você não possui permissão para ${modalOperation === "delete" ? "excluir" : "restaurar"
      } fornecedores`,
      (error) => {
        setModalOperation(null);
        showToast(
          error.response?.data?.message || "Erro ao atualizar fornecedor",
          "error"
        );
      }
    ),
    onSuccess: (response) => {
      if (modalOperation === "restore") {
        showToast(
          <>
            Fornecedor <b>{fornecedorData?.codigo}</b> restaurado com sucesso!
          </>,
          "success"
        );
        refetch();
        setModalOperation(null);
      } else {
        // Reset dirty state antes de navegar
        isDirtyRef.current = false;

        navigate("/cadastros/fornecedores", {
          state: {
            deleteSuccess: true,
            deletedName: fornecedorData?.nome || fornecedorData?.razaoSocial,
            deletedCode: fornecedorData?.codigo,
            timestamp: new Date().getTime(),
          },
        });
      }
    },
  });

  const handleDeleteOrRestore = (isDelete: boolean) => {
    setModalOperation(isDelete ? "delete" : "restore");
  };

  const handleConfirmOperation = async () => {
    if (modalOperation === "delete" && fornecedorData?.isDeleted) {
      setModalOperation("restore");
    }
    await deleteFornecedorMutation.mutate();
  };

  const handleCancelOperation = () => {
    setModalOperation(null);
  };

  // Handler para submissão do formulário
  const onSubmit: SubmitHandler<AlterarFornecedorDtoType> = (data) => {
    form.clearErrors();

    // Validações...
    if (data.tipoPessoa === TipoPessoa.Juridica) {
      if (!data.razaoSocial?.trim()) {
        form.setError("razaoSocial", {
          type: "manual",
          message: "Razão Social é obrigatória para Pessoa Jurídica",
        });
        return;
      }

      const cnpjLimpo = data.cnpj?.replace(/\D/g, "") || "";
      if (cnpjLimpo.length !== 14) {
        form.setError("cnpj", {
          type: "manual",
          message: "CNPJ deve conter 14 dígitos",
        });
        return;
      }
    } else if (data.tipoPessoa === TipoPessoa.Fisica) {
      if (!data.nome?.trim()) {
        form.setError("nome", {
          type: "manual",
          message: "Nome é obrigatório para Pessoa Física",
        });
        return;
      }

      const cpfLimpo = data.cpf?.replace(/\D/g, "") || "";
      if (cpfLimpo.length !== 11) {
        form.setError("cpf", {
          type: "manual",
          message: "CPF deve conter 11 dígitos",
        });
        return;
      }
    }

    // Verificar se o município foi selecionado
    if (!data.endereco.municipioId || data.endereco.municipioId <= 0) {
      form.setError("endereco.municipio", {
        type: "manual",
        message: "Município é obrigatório",
      });
      return;
    }

    updateFornecedorMutation.mutate(data);
  };

  // useEffect para controlar o estado isDirtyRef após operações de mutação
  useEffect(() => {
    if (!updateFornecedorMutation.isPending) {
      // Se a mutação foi bem-sucedida, garantir que o formulário não seja marcado como sujo
      if (updateFornecedorMutation.isSuccess) {
        isDirtyRef.current = false;
      }
    }
  }, [updateFornecedorMutation.isPending, updateFornecedorMutation.isSuccess]);

  // useEffect para controlar o estado isDirtyRef após operações de delete/restore
  useEffect(() => {
    if (!deleteFornecedorMutation.isPending) {
      if (deleteFornecedorMutation.isSuccess) {
        isDirtyRef.current = false;
      }
    }
  }, [deleteFornecedorMutation.isPending, deleteFornecedorMutation.isSuccess]);

  // Função para lidar com a seleção de município
  const handleMunicipioSelect = (municipio: MunicipioType) => {
    // Preencher o municipioId como campo principal
    form.setValue("endereco.municipioId", municipio.id, { shouldDirty: true });

    // Também manter nome e UF para exibição
    form.setValue("endereco.municipio", municipio.nome, { shouldDirty: true });
    form.setValue("endereco.uf", municipio.uf, { shouldDirty: true });

    showToast(
      <>
        Município{" "}
        <strong>
          {municipio.nome}/{municipio.uf}
        </strong>{" "}
        selecionado com sucesso!
      </>,
      "success"
    );

    setShowMunicipioSelector(false);
  };

  if (isLoading) {
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Carregando...</span>
      </Spinner>
    );
  }

  if (isError) {
    return <div>Ocorreu um erro ao carregar os dados do fornecedor</div>;
  }

  const displayName =
    fornecedorData?.nome || fornecedorData?.razaoSocial || "Fornecedor";

  return (
    <div>
      <UnsavedChangesModal
        show={showNavigationModal}
        onConfirm={handleNavigationConfirm}
        onCancel={handleNavigationCancel}
      />

      <DeleteRestoreModal
        show={modalOperation !== null}
        isDeleteOperation={modalOperation === "delete"}
        onConfirm={handleConfirmOperation}
        onCancel={handleCancelOperation}
        isLoading={deleteFornecedorMutation.isPending}
        itemName={displayName}
        itemCode={fornecedorData?.codigo}
        titleMessage={
          modalOperation === "delete"
            ? "Confirmar Exclusão"
            : "Restaurar Fornecedor"
        }
        confirmMessage={
          modalOperation === "delete"
            ? "Você está prestes a excluir o fornecedor:"
            : "Você está prestes a restaurar o acesso do fornecedor:"
        }
        infoMessage={
          modalOperation === "delete"
            ? "Ao excluir este fornecedor, ele será marcado como inativo e não poderá ser usado em outras partes do sistema. Esta ação poderá ser revertida posteriormente pelo administrador."
            : "Ao restaurar este fornecedor, ele voltará a ser ativo no sistema. Todas as configurações e associações serão preservadas."
        }
        actionButtonText={
          modalOperation === "delete"
            ? "Excluir Fornecedor"
            : "Restaurar Fornecedor"
        }
      />

      <MunicipioSelector
        show={showMunicipioSelector}
        onHide={() => setShowMunicipioSelector(false)}
        onSelect={handleMunicipioSelect}
      />

      <PageBreadcrumb
        items={fornecedorBreadcrumbItems(fornecedorData?.codigo)}
      />

      <FormProvider {...form}>
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Editar Fornecedor</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Atualize as informações do fornecedor
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {!fornecedorData?.isDeleted && (
                <Button
                  variant="outline-danger"
                  className="me-2"
                  onClick={() => handleDeleteOrRestore(true)}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Excluir
                </Button>
              )}
              <Button
                variant="primary"
                type="submit"
                disabled={updateFornecedorMutation.isPending}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {updateFornecedorMutation.isPending
                  ? "Salvando..."
                  : "Salvar Alterações"}
              </Button>
            </div>
          </div>

          {fornecedorData?.isDeleted && (
            <DeletedItemBanner
              title="Este fornecedor foi excluído e não está mais ativo no sistema"
              message="Todas as informações são apresentadas apenas para referência. Para editar o fornecedor, restaure o acesso primeiro."
              deletedAt={fornecedorData?.deletedAt}
              onRestore={() => handleDeleteOrRestore(false)}
              restoreButtonText="Restaurar Fornecedor"
              badgeText="Fornecedor Inativo"
            />
          )}

          {/* Informações do fornecedor */}
          <DeletedBorderCard isDeleted={fornecedorData?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faBuilding} className="me-2" />
                Informações do Fornecedor
              </h5>

              <Row>
                <Col md={3} className="mb-3 mb-md-0">
                  <Form.Group controlId="tipoPessoa">
                    <Form.Label className="d-block mb-2">
                      Tipo de Pessoa <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="d-flex flex-column gap-2">
                      <Card
                        className={`border cursor-pointer ${tipoPessoaSelecionado === TipoPessoa.Juridica
                          ? "border-primary"
                          : "border-light"
                          }`}
                        onClick={() =>
                          !fornecedorData?.isDeleted &&
                          setTipoPessoaSelecionado(TipoPessoa.Juridica)
                        }
                        style={{
                          transition: "all 0.2s ease-in-out",
                          cursor: fornecedorData?.isDeleted
                            ? "not-allowed"
                            : "pointer",
                          opacity: fornecedorData?.isDeleted ? 0.6 : 1,
                        }}
                      >
                        <Card.Body className="p-2">
                          <div className="d-flex align-items-center">
                            <div
                              className={`d-flex justify-content-center align-items-center rounded-circle me-2`}
                              style={{
                                width: "36px",
                                height: "36px",
                                backgroundColor:
                                  tipoPessoaSelecionado === TipoPessoa.Juridica
                                    ? "#E7F1FF"
                                    : "#f8f9fa",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faBuilding}
                                className={`${tipoPessoaSelecionado === TipoPessoa.Juridica
                                  ? "text-primary"
                                  : "text-secondary"
                                  }`}
                              />
                            </div>
                            <div>
                              <div className="fw-medium">Pessoa Jurídica</div>
                              <div className="small text-muted">
                                Empresas e organizações
                              </div>
                            </div>
                            {tipoPessoaSelecionado === TipoPessoa.Juridica && (
                              <div className="ms-auto">
                                <FontAwesomeIcon
                                  icon={faCheckCircle}
                                  className="text-primary"
                                />
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>

                      <Card
                        className={`border cursor-pointer ${tipoPessoaSelecionado === TipoPessoa.Fisica
                          ? "border-primary"
                          : "border-light"
                          }`}
                        onClick={() =>
                          !fornecedorData?.isDeleted &&
                          setTipoPessoaSelecionado(TipoPessoa.Fisica)
                        }
                        style={{
                          transition: "all 0.2s ease-in-out",
                          cursor: fornecedorData?.isDeleted
                            ? "not-allowed"
                            : "pointer",
                          opacity: fornecedorData?.isDeleted ? 0.6 : 1,
                        }}
                      >
                        <Card.Body className="p-2">
                          <div className="d-flex align-items-center">
                            <div
                              className={`d-flex justify-content-center align-items-center rounded-circle me-2`}
                              style={{
                                width: "36px",
                                height: "36px",
                                backgroundColor:
                                  tipoPessoaSelecionado === TipoPessoa.Fisica
                                    ? "#E7F1FF"
                                    : "#f8f9fa",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faUser}
                                className={`${tipoPessoaSelecionado === TipoPessoa.Fisica
                                  ? "text-primary"
                                  : "text-secondary"
                                  }`}
                              />
                            </div>
                            <div>
                              <div className="fw-medium">Pessoa Física</div>
                              <div className="small text-muted">Indivíduos</div>
                            </div>
                            {tipoPessoaSelecionado === TipoPessoa.Fisica && (
                              <div className="ms-auto">
                                <FontAwesomeIcon
                                  icon={faCheckCircle}
                                  className="text-primary"
                                />
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                    <Form.Control
                      type="hidden"
                      {...form.register("tipoPessoa")}
                    />
                  </Form.Group>
                </Col>

                <Col md={9} className="border-start border-light ps-md-4">
                  {/* Campos específicos para Pessoa Jurídica */}
                  <div
                    className="form-section-transition mt-3"
                    style={{
                      opacity:
                        tipoPessoaSelecionado === TipoPessoa.Juridica ? 1 : 0,
                      maxHeight:
                        tipoPessoaSelecionado === TipoPessoa.Juridica
                          ? "1000px"
                          : "0",
                      marginBottom:
                        tipoPessoaSelecionado === TipoPessoa.Juridica
                          ? "1rem"
                          : "0",
                    }}
                  >
                    {tipoPessoaSelecionado === TipoPessoa.Juridica && (
                      <>
                        <Row className="g-3">
                          <Col md={3}>
                            <Form.Group controlId="codigo">
                              <Form.Label>Código</Form.Label>
                              <Form.Control
                                placeholder="Código"
                                value={fornecedorData?.codigo || ""}
                                disabled
                              />
                            </Form.Group>
                          </Col>

                          <Col md={9}>
                            <Form.Group controlId="razaoSocial">
                              <Form.Label>
                                Razão Social{" "}
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                placeholder="Razão Social"
                                {...form.register("razaoSocial", {
                                  required: "Razão Social é obrigatória",
                                })}
                                isInvalid={!!form.formState.errors.razaoSocial}
                                disabled={fornecedorData?.isDeleted}
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.razaoSocial?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>{" "}
                        <Row className="g-3 mt-2">
                          <Col md={6}>
                            <Form.Group controlId="nomeFantasia">
                              <Form.Label>Nome Fantasia</Form.Label>
                              <Form.Control
                                placeholder="Nome Fantasia"
                                {...form.register("nomeFantasia")}
                                isInvalid={!!form.formState.errors.nomeFantasia}
                                disabled={fornecedorData?.isDeleted}
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.nomeFantasia?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="cnpj">
                              <Form.Label>
                                CNPJ <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                placeholder="00.000.000/0000-00"
                                {...form.register("cnpj", {
                                  required: "CNPJ é obrigatório",
                                  onChange: (e) => {
                                    const formatted = formatCnpj(
                                      e.target.value
                                    );
                                    e.target.value = formatted;
                                  },
                                })}
                                isInvalid={!!form.formState.errors.cnpj}
                                disabled={fornecedorData?.isDeleted}
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.cnpj?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="g-3 mt-2">
                          <Col md={6}>
                            <Form.Group controlId="inscricaoEstadual">
                              <Form.Label>Inscrição Estadual</Form.Label>
                              <Form.Control
                                placeholder="Inscrição Estadual"
                                {...form.register("inscricaoEstadual")}
                                isInvalid={
                                  !!form.formState.errors.inscricaoEstadual
                                }
                                disabled={fornecedorData?.isDeleted}
                              />
                              <Form.Control.Feedback type="invalid">
                                {
                                  form.formState.errors.inscricaoEstadual
                                    ?.message
                                }
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>

                  {/* Campos específicos para Pessoa Física */}
                  <div
                    className="form-section-transition"
                    style={{
                      opacity:
                        tipoPessoaSelecionado === TipoPessoa.Fisica ? 1 : 0,
                      maxHeight:
                        tipoPessoaSelecionado === TipoPessoa.Fisica
                          ? "1000px"
                          : "0",
                      marginBottom:
                        tipoPessoaSelecionado === TipoPessoa.Fisica
                          ? "1rem"
                          : "0",
                    }}
                  >
                    {tipoPessoaSelecionado === TipoPessoa.Fisica && (
                      <>
                        <Row className="g-3">
                          <Col md={3}>
                            <Form.Group controlId="codigo">
                              <Form.Label>Código</Form.Label>
                              <Form.Control
                                placeholder="Código"
                                value={fornecedorData?.codigo || ""}
                                disabled
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={9}>
                            <Form.Group controlId="nome">
                              <Form.Label>
                                Nome <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                placeholder="Nome Completo"
                                {...form.register("nome", {
                                  required: "Nome é obrigatório",
                                })}
                                isInvalid={!!form.formState.errors.nome}
                                disabled={fornecedorData?.isDeleted}
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.nome?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>{" "}
                        <Row className="g-3 mt-2">
                          <Col md={6}>
                            <Form.Group controlId="cpf">
                              <Form.Label>
                                CPF <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                placeholder="000.000.000-00"
                                {...form.register("cpf", {
                                  required: "CPF é obrigatório",
                                  onChange: (e) => {
                                    const formatted = formatCpf(e.target.value);
                                    e.target.value = formatted;
                                  },
                                })}
                                isInvalid={!!form.formState.errors.cpf}
                                disabled={fornecedorData?.isDeleted}
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.cpf?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group controlId="dataNascimento">
                              <Form.Label>Data de Nascimento</Form.Label>
                              <Form.Control
                                type="date"
                                {...form.register("dataNascimento")}
                                isInvalid={
                                  !!form.formState.errors.dataNascimento
                                }
                                disabled={fornecedorData?.isDeleted}
                              />
                              <Form.Control.Feedback type="invalid">
                                {form.formState.errors.dataNascimento?.message}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>

                  {/* Campos de contato comuns */}
                  <div className="mt-4">
                    <div className="border-start border-2 border-primary ps-2 mb-3">
                      <h6 className="text-primary mb-0">
                        Informações de Contato
                      </h6>
                      <p className="text-muted small mb-0">
                        Dados de contato do fornecedor
                      </p>
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group controlId="telefone">
                          <Form.Label>
                            Telefone <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            placeholder="(00) 0000-0000"
                            {...form.register("telefone", {
                              required: "Telefone é obrigatório",
                              onChange: (e) => {
                                const formatted = formatTelefone(
                                  e.target.value
                                );
                                e.target.value = formatted;
                              },
                            })}
                            isInvalid={!!form.formState.errors.telefone}
                            disabled={fornecedorData?.isDeleted}
                          />
                          <Form.Control.Feedback type="invalid">
                            {form.formState.errors.telefone?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="celular">
                          <Form.Label>Celular</Form.Label>
                          <Form.Control
                            placeholder="(00) 00000-0000"
                            {...form.register("celular", {
                              onChange: (e) => {
                                const formatted = formatTelefone(
                                  e.target.value
                                );
                                e.target.value = formatted;
                              },
                            })}
                            isInvalid={!!form.formState.errors.celular}
                            disabled={fornecedorData?.isDeleted}
                          />
                          <Form.Control.Feedback type="invalid">
                            {form.formState.errors.celular?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="email@exemplo.com"
                            {...form.register("email")}
                            isInvalid={!!form.formState.errors.email}
                            disabled={fornecedorData?.isDeleted}
                          />
                          <Form.Control.Feedback type="invalid">
                            {form.formState.errors.email?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </DeletedBorderCard>

          {/* Funções do fornecedor */}
          <DeletedBorderCard isDeleted={fornecedorData?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                Funções do Fornecedor
              </h5>

              <Row className="g-3">
                <Col md={6}>
                  <div className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="isFornecedorProduto"
                      label={<span>Fornecedor de Produto</span>}
                      {...form.register("isFornecedorProduto")}
                      className="mb-1"
                      disabled={fornecedorData?.isDeleted}
                    />
                    <div className="text-muted small ps-4">
                      Poderá ser usado em sugestões de compras e pedidos
                    </div>
                  </div>

                  <div className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="isCliente"
                      label={<span>Cliente</span>}
                      {...form.register("isCliente")}
                      className="mb-1"
                      disabled={fornecedorData?.isDeleted}
                    />
                    <div className="text-muted small ps-4">
                      Fornecedor também será registrado como cliente no sistema
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </DeletedBorderCard>

          {/* Informações de Compra */}
          <DeletedBorderCard isDeleted={fornecedorData?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                Informações de Compra
              </h5>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="prazoEntrega">
                    <Form.Label>Prazo de Entrega (em dias)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0"
                      min="0"
                      {...form.register("prazoEntrega", {
                          valueAsNumber: true
                      })}
                      className="mb-1"
                      disabled={
                        !isFornecedorProduto || fornecedorData?.isDeleted
                      }
                    />
                    <Form.Text className="text-muted">
                      Tempo médio para entrega após aprovação do pedido
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="valorMinimo">
                    <Form.Label>Valor Mínimo para Compra (R$)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0,00"
                      min="0"
                      step="0.01"
                      {...form.register("valorMinimo", {
                          valueAsNumber: true
                      })}
                      className="mb-1"
                      disabled={
                        !isFornecedorProduto || fornecedorData?.isDeleted
                      }
                    />
                    <Form.Text className="text-muted">
                      Valor mínimo exigido para realização de pedidos
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </DeletedBorderCard>

          {/* Endereço do fornecedor */}
          <DeletedBorderCard isDeleted={fornecedorData?.isDeleted}>
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                Endereço
              </h5>

              <Row className="g-3">
                <Col md={4}>
                  <Form.Group controlId="cep">
                    <Form.Label>
                      CEP <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      placeholder="00000-000"
                      {...form.register("endereco.cep", {
                        required: "CEP é obrigatório",
                        onChange: (e) => {
                          const formatted = formatCep(e.target.value);
                          e.target.value = formatted;
                        },
                      })}
                      isInvalid={!!form.formState.errors.endereco?.cep}
                      disabled={fornecedorData?.isDeleted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.cep?.message}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Digite o CEP para autocompletar o endereço
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col md={8}>
                  <Form.Group controlId="logradouro">
                    <Form.Label>
                      Logradouro <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      placeholder="Logradouro"
                      {...form.register("endereco.logradouro", {
                        required: "Logradouro é obrigatório",
                      })}
                      isInvalid={!!form.formState.errors.endereco?.logradouro}
                      disabled={fornecedorData?.isDeleted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.logradouro?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId="numero">
                    <Form.Label>
                      Número <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      placeholder="Número"
                      {...form.register("endereco.numero", {
                        required: "Número é obrigatório",
                      })}
                      isInvalid={!!form.formState.errors.endereco?.numero}
                      disabled={fornecedorData?.isDeleted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.numero?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col md={12}>
                  <Form.Group controlId="complemento">
                    <Form.Label>Complemento</Form.Label>
                    <Form.Control
                      placeholder="Complemento"
                      {...form.register("endereco.complemento")}
                      isInvalid={!!form.formState.errors.endereco?.complemento}
                      disabled={fornecedorData?.isDeleted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.complemento?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col md={4}>
                  <Form.Group controlId="bairro">
                    <Form.Label>
                      Bairro <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      placeholder="Bairro"
                      {...form.register("endereco.bairro", {
                        required: "Bairro é obrigatório",
                      })}
                      isInvalid={!!form.formState.errors.endereco?.bairro}
                      disabled={fornecedorData?.isDeleted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.endereco?.bairro?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={5}>
                  <Form.Group controlId="municipio">
                    <Form.Label>
                      Cidade <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="input-group input-group-municipio">
                      <Form.Control
                        placeholder="Clique para selecionar município"
                        {...form.register("endereco.municipio", {
                          required: "Cidade é obrigatória",
                        })}
                        style={{ cursor: "pointer" }}
                        isInvalid={!!form.formState.errors.endereco?.municipio}
                        disabled
                        onClick={() =>
                          !fornecedorData?.isDeleted &&
                          setShowMunicipioSelector(true)
                        }
                      />
                      <Button
                        variant="primary"
                        onClick={() => setShowMunicipioSelector(true)}
                        title="Buscar município"
                        className="d-flex align-items-center rounded-end"
                        disabled={fornecedorData?.isDeleted}
                      >
                        <FontAwesomeIcon icon={faSearch} className="me-1" />
                        Selecionar
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.endereco?.municipio?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group controlId="uf">
                    <Form.Label>
                      UF <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        placeholder="UF"
                        {...form.register("endereco.uf", {
                          required: "UF é obrigatória",
                        })}
                        style={{ cursor: "pointer" }}
                        isInvalid={!!form.formState.errors.endereco?.uf}
                        disabled
                        onClick={() =>
                          !fornecedorData?.isDeleted &&
                          setShowMunicipioSelector(true)
                        }
                      />
                      {form.watch("endereco.uf") && (
                        <span
                          className="position-absolute end-0 top-0 d-flex align-items-center h-100 px-3 text-muted"
                          style={{ pointerEvents: "none" }}
                        >
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                        </span>
                      )}
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.endereco?.uf?.message}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Campo oculto para o ID do município */}
              <Form.Control type="hidden" {...form.register("endereco.municipioId")} />
            </Card.Body>
          </DeletedBorderCard>

          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <Button
              variant="secondary"
              type="button"
              onClick={() =>
                navigate("/cadastros/fornecedores", {
                  state: { needsRefresh: true },
                })
              }
            >
              Cancelar
            </Button>
            <div className="d-flex flex-wrap gap-2">
              {!fornecedorData?.isDeleted && (
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={() => handleDeleteOrRestore(true)}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Excluir Fornecedor
                </Button>
              )}
              <Button
                variant="primary"
                type="submit"
                disabled={updateFornecedorMutation.isPending}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {updateFornecedorMutation.isPending
                  ? "Salvando..."
                  : "Salvar Alterações"}
              </Button>
              {fornecedorData?.isDeleted && (
                <Button
                  variant="danger"
                  onClick={() => handleDeleteOrRestore(false)}
                  className="rounded-pill px-4"
                >
                  <FontAwesomeIcon icon={faUnlock} className="me-2" />
                  Restaurar Fornecedor
                </Button>
              )}
            </div>
          </div>
        </Form>
      </FormProvider>
    </div>
  );
};

export default EditarFornecedor;
